import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Building2, CreditCard, ShoppingBag, DollarSign, Database, TrendingUp, TrendingDown, Minus, PieChart as PieIcon } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API_BASE_URL } from '@/config/api';
import useDashboard from '@/hooks/useDashboard';

const API = API_BASE_URL;

const PERIOD_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last_7_days', label: 'Last 7 Days' },
  { value: 'last_30_days', label: 'Last 30 Days' },
  { value: 'this_month', label: 'This Month' },
  { value: 'custom', label: 'Custom Range' },
];

const KPI_SHELL = [
  { key: 'sk0' },
  { key: 'sk1' },
  { key: 'sk2' },
  { key: 'sk3' },
];

const TrendPill = ({ trend }) => {
  if (!trend || !trend.comparable || trend.delta_percentage === null) {
    return (
      <div className="mt-2 inline-flex items-center rounded-full bg-slate-100 px-2 py-1 text-xs text-slate-600" style={{ fontFamily: 'Figtree, sans-serif' }}>
        No previous data
      </div>
    );
  }

  const delta = Number(trend.delta_percentage || 0);
  const positive = delta > 0;
  const negative = delta < 0;
  const Icon = positive ? TrendingUp : negative ? TrendingDown : Minus;
  const color = positive ? '#10B981' : negative ? '#EF4444' : '#64748B';

  return (
    <div className="mt-2 inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs" style={{ fontFamily: 'Figtree, sans-serif', color, backgroundColor: positive ? '#ECFDF3' : negative ? '#FEF2F2' : '#F1F5F9' }}>
      <Icon className="h-3.5 w-3.5" />
      <span>{positive ? '+' : ''}{delta.toFixed(1)}%</span>
    </div>
  );
};

const EmptyChartState = ({ message }) => {
  return (
    <div className="flex h-[300px] flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border/60 bg-muted/20 text-center">
      <PieIcon className="h-8 w-8 text-muted-foreground" />
      <p className="text-sm text-muted-foreground" style={{ fontFamily: 'Figtree, sans-serif' }}>
        {message}
      </p>
    </div>
  );
};

const SuperAdminDashboard = () => {
  const [seeding, setSeeding] = useState(false);
  const [period, setPeriod] = useState('last_7_days');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [appliedCustomStartDate, setAppliedCustomStartDate] = useState('');
  const [appliedCustomEndDate, setAppliedCustomEndDate] = useState('');

  const startDate = period === 'custom' ? appliedCustomStartDate : undefined;
  const endDate = period === 'custom' ? appliedCustomEndDate : undefined;
  const customRangeReady = period !== 'custom' || Boolean(appliedCustomStartDate && appliedCustomEndDate);

  const { data, loading, error, refetch } = useDashboard({
    autoFetch: customRangeReady,
    period,
    startDate,
    endDate,
  });
  const [lastUpdated, setLastUpdated] = useState(null);
  const stats = data?.super_admin_dashboard?.stats || null;
  const trends = data?.super_admin_dashboard?.stats_trends || {};
  const dateRange = data?.date_range || null;
  const safeStats = stats || {
    total_restaurants: 0,
    active_subscriptions: 0,
    total_orders: 0,
    monthly_revenue: 0,
    plan_distribution: [],
  };

  const selectedPeriodLabel = useMemo(() => {
    return PERIOD_OPTIONS.find((item) => item.value === period)?.label || 'Last 7 Days';
  }, [period]);

  const applyCustomRange = () => {
    setAppliedCustomStartDate(customStartDate);
    setAppliedCustomEndDate(customEndDate);
  };

  useEffect(() => {
    if (data) {
      setLastUpdated(new Date());
    }
  }, [data]);

  const handleSeedDemo = async () => {
    setSeeding(true);
    try {
      const { data } = await axios.post(`${API}/admin/seed-demo`, { force: true }, { withCredentials: true });
      if (data.skipped) {
        toast.info(data.reason);
      } else {
        toast.success(`Demo data created: ${data.companies} companies, ${data.restaurants} restaurants, ${data.menu_items} menu items, ${data.orders} orders`);
      }
      await refetch();
    } catch (error) {
      toast.error('Failed to seed demo data');
    } finally {
      setSeeding(false);
    }
  };

  if (error) return <div className="text-center py-8">Failed to load dashboard data.</div>;
  if (!loading && !stats) return <div className="text-center py-8">Loading...</div>;

  const statCards = [
    { key: 'total_restaurants', title: 'Total Restaurants', value: safeStats.total_restaurants, icon: Building2, color: '#232D4B' },
    { key: 'active_subscriptions', title: 'Active Subscriptions', value: safeStats.active_subscriptions, icon: CreditCard, color: '#E57200' },
    { key: 'total_orders', title: `Total Orders (${dateRange?.label || selectedPeriodLabel})`, value: safeStats.total_orders, icon: ShoppingBag, color: '#10B981' },
    { key: 'monthly_revenue', title: `Revenue (${dateRange?.label || selectedPeriodLabel})`, value: `$${Number(safeStats.monthly_revenue || 0).toLocaleString()}`, icon: DollarSign, color: '#F59E0B' },
  ];

  const COLORS = ['#232D4B', '#E57200', '#10B981', '#F59E0B'];

  return (
    <div className="space-y-6" data-testid="super-admin-dashboard">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', color: '#232D4B' }}>
            Super Admin Overview
          </h1>
          <p className="text-muted-foreground mt-1" style={{ fontFamily: 'Figtree, sans-serif' }}>
            Platform-wide analytics and management
          </p>
        </div>
        <Button onClick={handleSeedDemo} disabled={seeding} variant="outline" data-testid="seed-demo-btn">
          <Database className="h-4 w-4 me-2" />
          {seeding ? 'Seeding...' : 'Reseed Demo Data'}
        </Button>
      </div>

      <div className="flex flex-col md:flex-row md:items-end gap-3">
        <div className="w-full md:w-64">
          <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground mb-1" style={{ fontFamily: 'Figtree, sans-serif' }}>
            Date Range
          </div>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger>
              <SelectValue placeholder="Select period" />
            </SelectTrigger>
            <SelectContent>
              {PERIOD_OPTIONS.map((item) => (
                <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {period === 'custom' && (
          <>
            <div>
              <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground mb-1" style={{ fontFamily: 'Figtree, sans-serif' }}>
                Start
              </div>
              <Input type="date" value={customStartDate} onChange={(e) => setCustomStartDate(e.target.value)} />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground mb-1" style={{ fontFamily: 'Figtree, sans-serif' }}>
                End
              </div>
              <Input type="date" value={customEndDate} onChange={(e) => setCustomEndDate(e.target.value)} />
            </div>
            <Button
              className="md:self-end"
              style={{ backgroundColor: '#E57200' }}
              onClick={applyCustomRange}
              disabled={!customStartDate || !customEndDate}
            >
              Apply
            </Button>
          </>
        )}
      </div>

      <div className="text-sm text-muted-foreground" style={{ fontFamily: 'Figtree, sans-serif' }}>
        {`Showing data for ${dateRange?.label || selectedPeriodLabel}. Comparing with previous equivalent period.`}
      </div>

      <div className="text-xs text-muted-foreground" style={{ fontFamily: 'Figtree, sans-serif' }}>
        {`Last updated: ${lastUpdated ? lastUpdated.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '--:--'}`}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {loading ? KPI_SHELL.map((item, i) => (
          <Card key={item.key} className="border-border/40" data-testid={`super-stat-skeleton-${i}`}>
            <CardHeader className="pb-2">
              <Skeleton className="h-3 w-28" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-7 w-20" />
              <Skeleton className="mt-3 h-5 w-24 rounded-full" />
            </CardContent>
          </Card>
        )) : statCards.map((stat, i) => {
          const Icon = stat.icon;
          const trend = trends?.[stat.key];
          return (
            <Card key={i} className="border-border/40 transition-all duration-200 hover:-translate-y-1 hover:shadow-lg" data-testid={`super-stat-${i}`}>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground" style={{ fontFamily: 'Figtree, sans-serif' }}>
                  {stat.title}
                </CardTitle>
                <Icon className="h-4 w-4" style={{ color: stat.color }} />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: stat.color }}>
                  {stat.value}
                </div>
                <TrendPill trend={trend} />
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-border/40">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Subscription Plan Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-[300px] w-full" />
            ) : (safeStats.plan_distribution || []).length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={safeStats.plan_distribution || []}
                    dataKey="count"
                    nameKey="_id"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label
                  >
                    {(safeStats.plan_distribution || []).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChartState message="No subscriptions during this period." />
            )}
          </CardContent>
        </Card>

        <Card className="border-border/40">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Platform Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3" style={{ fontFamily: 'Figtree, sans-serif' }}>
              <div className="flex justify-between items-center pb-3 border-b border-border/40">
                <span className="text-muted-foreground">Active Companies</span>
                <span className="font-bold" style={{ color: '#232D4B' }}>{safeStats.active_subscriptions}</span>
              </div>
              <div className="flex justify-between items-center pb-3 border-b border-border/40">
                <span className="text-muted-foreground">Total Restaurants Onboarded</span>
                <span className="font-bold" style={{ color: '#232D4B' }}>{safeStats.total_restaurants}</span>
              </div>
              <div className="flex justify-between items-center pb-3 border-b border-border/40">
                <span className="text-muted-foreground">Orders Processed</span>
                <span className="font-bold" style={{ color: '#232D4B' }}>{safeStats.total_orders.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">This Month Revenue</span>
                <span className="font-bold text-xl" style={{ color: '#E57200' }}>${safeStats.monthly_revenue.toLocaleString()}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
